import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Users,
  Boxes,
  Receipt,
  UsersRound,
  Star,
  ScrollText,
  Settings,
  UserRound,
  Bell,
  BarChart3,
  LineChart,
  type LucideIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { usePermissions } from "@/hooks/usePermissions";
import { NavUser } from "./nav-user";
import { OrgSwitcher } from "./org-switcher";

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  enabled: boolean;
  /**
   * Hide the entry entirely when the user lacks this flag (doc 23-billing §12) — the
   * platform's stated intent is "hidden entirely, not just disabled". Omitted = always
   * visible. A Provider Admin holds every flag, so nothing is hidden from them.
   */
  requires?: PermissionFlag;
}

type PermissionFlag = "canManageInvoices" | "canViewFinancials";

// v2 is the shipped surface — the v1 duplicates are dropped from the sidebar. Only
// Profile has no v2 counterpart, so it stays.
const NAV: NavItem[] = [
  {
    label: "Dashboard",
    to: "/provider/dashboard",
    icon: LayoutDashboard,
    enabled: true,
  },
  { label: "Catalogue", to: "/provider/catalogue", icon: Boxes, enabled: true },
  { label: "Clients", to: "/provider/clients", icon: Users, enabled: true },
  {
    label: "Subscriptions",
    to: "/provider/subscriptions",
    icon: Receipt,
    enabled: true,
  },
  { label: "Reviews", to: "/provider/reviews", icon: Star, enabled: true },
  // One top-level Billing entry; Run billing / Billing runs / Revenue are reached
  // through the section nav on the Billing pages (BillingNav), not the sidebar.
  {
    label: "Billing",
    to: "/provider/billing",
    icon: Receipt,
    enabled: true,
    requires: "canViewFinancials",
  },
  {
    label: "Analytics",
    to: "/provider/analytics",
    icon: LineChart,
    enabled: true,
  },
  { label: "Reports", to: "/provider/reports", icon: BarChart3, enabled: true },
  { label: "Audit", to: "/provider/audit", icon: ScrollText, enabled: true },
  {
    label: "Notifications",
    to: "/provider/notifications",
    icon: Bell,
    enabled: true,
  },
  { label: "Jobs", to: "/provider/jobs", icon: Boxes, enabled: true },
  {
    label: "Settings",
    to: "/provider/settings",
    icon: Settings,
    enabled: true,
  },
  { label: "Team", to: "/provider/team", icon: UsersRound, enabled: true },
  { label: "Profile", to: "/provider/profile", icon: UserRound, enabled: true },
];

/** Provider Admin sidebar — shadcn Sidebar (adapted from shadcn-admin AppSidebar). */
export function AppSidebar(): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { can, isLoading } = usePermissions();

  // Permission-gated entries stay hidden until `me` resolves, so a user without the
  // flag never sees the item flash in and disappear.
  const nav = NAV.filter((i) => !i.requires || (!isLoading && can(i.requires)));

  // The active item is the longest `to` that matches, so nested routes like
  // /provider/catalogue/addons highlight Add-ons rather than Catalogue.
  const activeTo = nav
    .filter((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;

  const renderItem = (item: NavItem): JSX.Element => {
    const Icon = item.icon;
    const active = item.to === activeTo;
    return (
      <SidebarMenuItem key={item.to}>
        {item.enabled ? (
          <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
            <Link to={item.to}>
              <Icon />
              <span>{item.label}</span>
            </Link>
          </SidebarMenuButton>
        ) : (
          <SidebarMenuButton
            tooltip={`${item.label} — coming soon`}
            className="cursor-not-allowed opacity-40"
            aria-disabled
          >
            <Icon />
            <span>{item.label}</span>
          </SidebarMenuButton>
        )}
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <OrgSwitcher variant="sidebar" />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Platform</SidebarGroupLabel>
          <SidebarMenu>{nav.map(renderItem)}</SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser profileTo="/provider/profile" />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
